import React from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { FormProvider, useForm, useFormContext } from 'react-hook-form'
import { Button, ConfirmationModal, Input, InputCheck, SelectAnimalFetchInput, SelectInput } from 'src/components'
import { useStyles } from './styles'
import { formatCurrencyValue, formatCurrencyValueDirect, formatDateToInputValue } from 'src/utils'
import { useSaleDetail } from './_hooks/useSaleDetail'
import { useGetLotList } from 'src/hooks/api/lot'
import { SaleDTO } from 'src/dto/sale'
import { SaleParams } from 'src/domain/sale'
import { CircularProgress } from '@material-ui/core'
import { FormattedOption } from 'src/dto/animal.dto'
import { VALIDATIONS } from 'src/@types/validations'

interface PopupContentProps {
  onClose: () => void
  saleData: SaleDTO | undefined | null
}
type SalePayloadProps = Partial<SaleParams>

interface SaleDetailFormProps {
  onClose: () => void
  saleData: SaleDTO | undefined | null
}

const SaleDetail: React.FC<PopupContentProps> = ({ onClose, saleData }) => {
  const methods = useForm<SalePayloadProps>({
    mode: 'onSubmit',
    defaultValues: {
      ...saleData,
      valorArroba: saleData?.valorArroba ? formatCurrencyValueDirect(String(saleData.valorArroba)) : undefined,
      valorTotal: saleData?.valorTotal ? formatCurrencyValueDirect(String(saleData.valorTotal)) : undefined,
      tipoVenda: saleData?.idLote ? 'lote' : saleData?.tipoVenda || 'animal',
      data: saleData?.data
        ? new Date(saleData?.data || Date.now()).toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0]
    },
    resolver: yupResolver(VALIDATIONS.Sale)
  })

  return (
    <FormProvider {...methods}>
      <SaleDetailForm onClose={onClose} saleData={saleData} />
    </FormProvider>
  )
}

const SaleDetailForm: React.FC<SaleDetailFormProps> = ({ onClose, saleData }) => {
  const style = useStyles()

  const {
    handleSubmit,
    register,
    setValue,
    watch,
    clearErrors,
    formState: { errors }
  } = useFormContext<SalePayloadProps>()

  const {
    isLoading,
    onSubmit,
    calcTotalPriceLotWeigt,
    totalLotWeight,
    setMultiSale,
    multiSale,
    handleDeleteSale,
    showDeleteConfirmation,
    setShowDeleteConfirmation,
    confirmDeleteSale,
    isSaving,
    isDeleting
  } = useSaleDetail({
    saleData,
    onClose
  })

  const { data: lotList = [] } = useGetLotList()

  const saleType = watch('tipoVenda')

  const renderSaleTypeSpecificFields = () => {
    switch (saleType) {
      case 'animal':
        return (
          <>
            <div className={style.halfWidth}>
              <SelectAnimalFetchInput
                label='Animal'
                className='input_text'
                selectBy='id'
                showBy={['cdn', 'nome']}
                value={watch('animais')?.[0] || ''}
                disabled={!!saleData?.idVenda}
                hidden={!saleData?.idVenda ? ['VD', 'MT'] : []}
                // @ts-ignore
                error={errors?.animais?.message}
                placeholder='Selecionar um animal'
                onChange={(item: FormattedOption | null) => {
                  if (!item) {
                    setValue('animais', [])
                    clearErrors('animais')
                    return
                  }

                  setValue('animais', [item.id])
                  clearErrors('animais')
                }}
              />

              <div className='input_text'>
                <Input
                  label='Valor Total'
                  id='valor_total'
                  className='input_text'
                  value={watch('valorTotal') || ''}
                  disabled={!!saleData?.idVenda}
                  error={errors?.valorTotal?.message}
                  placeholder='R$ 0,00'
                  onChange={(value: string) => {
                    const formattedValue = formatCurrencyValue(value)
                    setValue('valorTotal', formattedValue)
                    clearErrors('valorTotal')
                  }}
                />
              </div>
            </div>
          </>
        )
      case 'lote':
        return (
          <>
            <div className={style.formControl}>
              <SelectInput
                label='Lote de Animais'
                selectBy='idLote'
                showBy={['descricao']}
                options={lotList}
                value={watch('idLote') || ''}
                disabled={!!saleData?.idVenda}
                error={errors?.idLote?.message}
                placeholder='Selecionar Lote de Animais'
                onChange={(item: any) => {
                  setValue('idLote', item.id)
                  setValue('animalList', item.animalList || [])
                }}
              />
            </div>
            <div className={style.halfWidth}>
              <Input
                label=' Valor da arroba'
                id='valor-arroba'
                disabled={!!saleData?.idVenda}
                className='input_text'
                value={watch('valorArroba') || ''}
                error={errors?.valorArroba?.message}
                placeholder='R$ 0,00'
                onChange={(value: string) => {
                  const formattedValue = formatCurrencyValue(value)
                  setValue('valorArroba', formattedValue)
                  calcTotalPriceLotWeigt()
                  clearErrors('valorArroba')
                }}
              />
              <div className='input_text'>
                <Input
                  label='Valor Total'
                  id='valor_total'
                  className='input_text'
                  value={watch('valorTotal') || ''}
                  readOnly
                />
                <p className={style.labelTotal}>
                  Peso total dos lotes: {isLoading ? <CircularProgress size={18} /> : `${totalLotWeight} KG`}
                </p>
              </div>
            </div>
          </>
        )
      default:
        return null
    }
  }

  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className={style.checkControl}>
          <InputCheck
            id='tipo-venda'
            type='radio'
            value='animal'
            label='Venda por animal'
            name='tipoVenda'
            disabled={!!saleData?.idVenda}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue('tipoVenda', e.target.value as 'animal')}
            register={register}
          />

          <InputCheck
            id='tipo-lote'
            type='radio'
            value='lote'
            label='Venda por lote'
            name='tipoVenda'
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue('tipoVenda', e.target.value as 'lote')}
            disabled={!!saleData?.idVenda}
            register={register}
          />
        </div>
        <fieldset>
          <div className={style.formControl}>
            <Input
              label='Data'
              id='date_venda'
              type='date'
              error={errors.data?.message}
              disabled={!!saleData?.idVenda}
              onChange={(value: string) => {
                const date = new Date(value).toISOString()
                setValue('data', date)
                clearErrors('data')
              }}
              value={formatDateToInputValue(watch('data') || '')}
            />
          </div>

          {renderSaleTypeSpecificFields()}

          <div className={style.formControl}>
            <Input
              label='Comprador'
              id='comprador'
              type='text'
              value={watch('comprador') || ''}
              error={errors.comprador?.message}
              onChange={(value: string) => {
                setValue('comprador', value)
                clearErrors('comprador')
              }}
            />
          </div>
        </fieldset>

        <div className='footerPopup'>
          {!saleData?.idVenda && (
            <div className={style.check}>
              <input
                id='register_sale'
                type='checkbox'
                checked={multiSale}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setMultiSale(event.target.checked)}
              />
              <label htmlFor='register_sale'>Cadastrar outra venda</label>
            </div>
          )}

          <div className={style.containerButtons}>
            <Button
              type='button'
              className={style.buttonWrapper}
              outlined
              onClick={onClose}
              disabled={isLoading || isSaving}
            >
              Cancelar
            </Button>
            {saleData?.idVenda && (
              <Button
                className={style.buttonWrapper}
                type='button'
                disabled={isSaving}
                outlined
                onClick={handleDeleteSale}
                style={{ backgroundColor: '#dc3545', color: 'white', borderColor: '#dc3545' }}
              >
                Excluir
              </Button>
            )}
            <Button className={style.buttonWrapper} type='submit' disabled={isLoading || isSaving} isLoading={isSaving}>
              {saleData?.idVenda ? 'Atualizar' : 'Salvar'}
            </Button>
          </div>
        </div>
      </form>

      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        title='Confirmar Exclusão'
        message='Tem certeza que deseja excluir esta venda?'
        onConfirm={confirmDeleteSale}
        onCancel={() => setShowDeleteConfirmation(false)}
        isLoading={isDeleting}
        confirmText='Confirmar'
        cancelText='Cancelar'
      />
    </div>
  )
}

export default SaleDetail
